<template>
  <div>
    <h1 class="outline--text">Import Rating&Geolocation</h1>
    <br />

    <v-card>
      <v-row class="mb-4" dense>
        <v-card-title>
          <v-text-field
            v-model="filters.admin"
            label="Admin name / email"
            clearable
          ></v-text-field>
        </v-card-title>
        <v-card-title>
          <v-select
            v-model="filters.status"
            :items="statusOptions"
            label="Status"
            clearable
          ></v-select>
        </v-card-title>

        <v-card-title>
          <v-btn color="primary" class="mr-2" @click="fetchSourceRatingsImport">
            Apply Filters
          </v-btn>
          <v-btn text @click="resetFilters"> Reset </v-btn>
        </v-card-title>

        <v-card-title>
          <source-rating-import-dialog
            v-if="canManage"
          ></source-rating-import-dialog>
        </v-card-title>
        <v-card-title>
          <source-geo-import-dialog v-if="canManage"></source-geo-import-dialog>
        </v-card-title>
      </v-row>

      <!-- Data loader -->
      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
        class="my-3"
      ></v-progress-linear>

      <v-data-table
        :headers="headers"
        :items="tableItems"
        :search="search"
        :loading="isLoading"
        :no-data-text="
          isLoading ? 'Loading items...' : 'No History Items found'
        "
        class="elevation-1"
      >
        <template v-slot:item.error_message="{ item }">
          <div>
            <span>
              {{
                isExpanded(item)
                  ? item.error_message
                  : truncate(item.error_message, 120)
              }}
            </span>

            <v-btn
              v-if="item.error_message && item.error_message.length > 120"
              icon
              x-small
              @click="toggleExpand(item)"
            >
              <v-icon small>
                {{ isExpanded(item) ? "mdi-chevron-up" : "mdi-chevron-down" }}
              </v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-snackbar v-model="showError" color="error" timeout="5000">
      {{ error }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showError = false"> Close </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  FETCH_COUNTRY_ISO_FILE,
  FETCH_SOURCE_RATING_IMPORT,
} from "@/store/modules/sourceRatingImport/actions";
import SourceRatingImportDialog from "@/views/sourceRating/item/SourceRatingImportDialog.vue";
import { EventBus } from "@/utils";
import {
  NEW_IMPORTED_SOURCE_GEO,
  NEW_IMPORTED_SOURCE_RATING,
} from "@/utils/events";
import { RESOURCE_SOURCE_RATING_MANAGEMENT } from "@/store/modules/admin/getters";
import acl from "@/mixin/acl";
import SourceGeoImportDialog from "@/views/sourceGeo/item/SourceGeoImportDialog.vue";

export default {
  name: "SourceRatingImportPage",
  mixins: [acl],
  components: { SourceGeoImportDialog, SourceRatingImportDialog },
  data() {
    return {
      search: "",
      showError: false,
      expandedErrors: {},
      filters: {
        admin: "",
        status: "",
      },
      headers: [
        { text: "Admin", value: "admin" },
        { text: "File name", value: "file_name" },
        { text: "Action Type", value: "action_type" },
        { text: "Total Rows", value: "rows_total" },
        { text: "Status", value: "status" },
        { text: "Success Rows", value: "rows_success" },
        { text: "Error Message", value: "error_message" },
        { text: "Date created", value: "created_at" },
      ],
      statusOptions: [
        { text: "Completed", value: "completed" },
        { text: "Completed with errors", value: "with_errors" },
        { text: "Failed", value: "failed" },
      ],
      dialog: false,
      editedEmail: null,
      editedData: {
        comment: "",
        is_actively_suppressed: 1,
      },
    };
  },
  computed: {
    ...mapGetters("sourceRatingImport", [
      "sourceRatingImport",
      "isLoading",
      "hasError",
      "error",
    ]),
    canManage: function () {
      return this.hasAccess([RESOURCE_SOURCE_RATING_MANAGEMENT]);
    },
    tableItems() {
      return this.sourceRatingImport?.data || [];
    },
  },
  methods: {
    fetchSourceRatingsImport() {
      const params = {
        admin: this.filters.admin || undefined,
        status: this.filters.status || undefined,
        search: this.search || undefined,
      };

      Object.keys(params).forEach((key) => {
        if (
          params[key] === undefined ||
          params[key] === null ||
          params[key] === ""
        ) {
          delete params[key];
        }
      });

      this.$store
        .dispatch(`sourceRatingImport/${FETCH_SOURCE_RATING_IMPORT}`, {
          params,
        })
        .catch((error) => {
          console.error("Error fetching Source Ratings:", error);
          this.showError = true;
        });
    },
    truncate(text, length) {
      if (!text) return "";
      return text.length > length ? text.substring(0, length) + "..." : text;
    },

    toggleExpand(item) {
      this.$set(this.expandedErrors, item.id, !this.expandedErrors[item.id]);
    },

    isExpanded(item) {
      return !!this.expandedErrors[item.id];
    },
    exportItems() {
      this.$store
        .dispatch(`sourceRatingImport/${FETCH_COUNTRY_ISO_FILE}`)
        .catch((error) => {
          console.error("Error exporting ISO Countries", error);
          this.showError = true;
        });
    },
    resetFilters() {
      this.filters.admin = "";
      this.filters.status = "";
      this.search = "";
      this.fetchSourceRatingsImport();
    },
  },
  mounted() {
    this.fetchSourceRatingsImport();
  },
  created() {
    [NEW_IMPORTED_SOURCE_RATING, NEW_IMPORTED_SOURCE_GEO].forEach((event) => {
      EventBus.$on(event, () => {
        this.fetchSourceRatingsImport();
      });
    });
  },
  watch: {
    error(newVal) {
      if (newVal) {
        this.showError = true;
      }
    },
  },
};
</script>
